import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, 
  Target,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Zap,
  Brain,
  Rocket,
  DollarSign,
  Shield,
  Clock,
  RefreshCw,
  Send,
  X,
  Info,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Calendar,
  Mail,
  PieChart,
  Activity,
  Briefcase,
  Heart,
  Smartphone,
  Globe,
  Cpu,
  Leaf,
  Building,
  Gamepad2,
  Stethoscope,
  GraduationCap,
  FileText,
  Phone,
  Book
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// Color palette aligned with main site
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// Innovation canvas sections with consistent styling
const CANVAS_SECTIONS = {
  problem: {
    title: 'Problem & Opportunity',
    icon: AlertTriangle,
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    textClass: 'text-red-700',
    iconBg: 'bg-red-100'
  },
  solution: {
    title: 'Solution Concept',
    icon: Lightbulb,
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-200',
    textClass: 'text-amber-700',
    iconBg: 'bg-amber-100'
  },
  value: {
    title: 'Value Proposition',
    icon: Target,
    bgClass: 'bg-green-50',
    borderClass: 'border-green-200',
    textClass: 'text-green-700',
    iconBg: 'bg-green-100'
  },
  market: {
    title: 'Target Market',
    icon: Users,
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-200',
    textClass: 'text-blue-700',
    iconBg: 'bg-blue-100'
  },
  resources: {
    title: 'Key Resources',
    icon: Zap,
    bgClass: 'bg-purple-50',
    borderClass: 'border-purple-200',
    textClass: 'text-purple-700',
    iconBg: 'bg-purple-100'
  },
  revenue: {
    title: 'Revenue Model',
    icon: DollarSign,
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-200',
    textClass: 'text-emerald-700',
    iconBg: 'bg-emerald-100'
  },
  metrics: {
    title: 'Success Metrics',
    icon: BarChart3,
    bgClass: 'bg-indigo-50',
    borderClass: 'border-indigo-200',
    textClass: 'text-indigo-700',
    iconBg: 'bg-indigo-100'
  },
  risks: {
    title: 'Key Risks',
    icon: Shield,
    bgClass: 'bg-orange-50',
    borderClass: 'border-orange-200',
    textClass: 'text-orange-700',
    iconBg: 'bg-orange-100'
  }
};

// Innovation examples for inspiration
const INNOVATION_EXAMPLES = [
  {
    title: "Smart Customer Service System",
    description: "Intelligent system that handles 80% of customer inquiries automatically using natural language processing",
    icon: <Brain className="h-4 w-4" />,
    color: "bg-blue-50 text-blue-700 border-blue-200",
    category: "Technology"
  },
  {
    title: "Sustainable Packaging Solution",
    description: "Biodegradable packaging alternative made from seaweed for e-commerce companies",
    icon: <Leaf className="h-4 w-4" />,
    color: "bg-green-50 text-green-700 border-green-200",
    category: "Sustainability"
  },
  {
    title: "Remote Work Collaboration Platform",
    description: "Virtual workspace that makes remote teams feel connected and productive",
    icon: <Users className="h-4 w-4" />,
    color: "bg-purple-50 text-purple-700 border-purple-200",
    category: "Workplace"
  },
  {
    title: "Personalized Health Monitoring",
    description: "Wearable device that tracks health patterns and predicts potential issues early",
    icon: <Heart className="h-4 w-4" />,
    color: "bg-red-50 text-red-700 border-red-200",
    category: "HealthTech"
  },
  {
    title: "Smart City Traffic Management",
    description: "Connected traffic system that reduces commute times and improves urban mobility",
    icon: <Activity className="h-4 w-4" />,
    color: "bg-amber-50 text-amber-700 border-amber-200",
    category: "Smart City"
  },
  {
    title: "Adaptive Learning Platform",
    description: "Educational system that adapts to each student's learning style and pace",
    icon: <GraduationCap className="h-4 w-4" />,
    color: "bg-indigo-50 text-indigo-700 border-indigo-200",
    category: "Education"
  }
];

// Enhanced Button component
const Button = ({ 
  children, 
  onClick, 
  isLoading = false, 
  fullWidth = false, 
  variant = 'primary', 
  icon, 
  className = '', 
  disabled = false,
  size = 'md'
}) => {
  const baseStyle = "font-medium transition-all duration-300 flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500";
  
  const sizes = {
    sm: "py-2 px-4 text-sm rounded-md",
    md: "py-3 px-6 text-base rounded-lg",
    lg: "py-4 px-8 text-lg rounded-xl"
  };
  
  const variants = {
    primary: `bg-red-600 hover:bg-red-700 text-white ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    outline: `bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
      ) : (
        icon && <span className="mr-2">{icon}</span>
      )}
      {children}
    </button>
  );
};

// Toast notification with better mobile support
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div className={`fixed top-20 left-4 right-4 sm:right-4 sm:left-auto sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 ${styles[type]}`}>
      <CheckCircle className="h-5 w-5 flex-shrink-0" />
      <p className="font-medium flex-1">{message}</p>
      <button 
        onClick={onClose} 
        className="flex-shrink-0 p-1 hover:bg-black/10 rounded"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Canvas Section Component with mobile improvements
const CanvasSection = ({ sectionKey, section, isExpanded, onToggle }) => {
  const sectionInfo = CANVAS_SECTIONS[sectionKey];
  const SectionIcon = sectionInfo.icon;

  return (
    <article className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      {/* Section Header */}
      <div className={`${sectionInfo.bgClass} px-4 sm:px-6 py-4 border-b ${sectionInfo.borderClass}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-lg ${sectionInfo.iconBg} flex items-center justify-center flex-shrink-0`}>
              <SectionIcon className={`h-5 w-5 ${sectionInfo.textClass}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-gray-900 text-base sm:text-lg">{sectionInfo.title}</h3>
              <p className="text-sm text-gray-600 truncate">{section.summary}</p>
            </div>
          </div>
          <button
            onClick={() => onToggle(sectionKey)}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0"
            aria-label={isExpanded ? "Collapse section" : "Expand section"}
          >
            {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Section Content */}
      <div className="p-4 sm:p-6">
        <p className="text-gray-700 mb-4 leading-relaxed">{section.description}</p>
        
        {/* Key Points */}
        {section.keyPoints && (
          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-3">Key Points:</h4>
            <ul className="space-y-2">
              {section.keyPoints.map((point, idx) => (
                <li key={idx} className="text-sm text-gray-600 flex items-start gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${sectionInfo.iconBg} mt-2 flex-shrink-0`}></div>
                  <span className="leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isExpanded && (
          <div className="pt-4 border-t border-gray-200">
            {/* Action Items */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Rocket className="h-4 w-4 text-red-600" />
                Recommended Actions:
              </h4>
              <ul className="space-y-2">
                {section.actions?.map((action, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-3">
                    <ArrowRight className="h-3 w-3 mt-1 text-gray-400 flex-shrink-0" />
                    <span className="leading-relaxed">{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// Loading Animation with better mobile support
const LoadingAnimation = () => {
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(1);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 - prev) / 12;
        return Math.min(newProgress, 99);
      });
      
      setStep(prev => {
        if (prev === 1 && progress > 25) return 2;
        if (prev === 2 && progress > 50) return 3;
        if (prev === 3 && progress > 75) return 4;
        return prev;
      });
    }, 700);
    
    return () => clearInterval(interval);
  }, [progress]);
  
  const steps = [
    { message: "Analyzing innovation opportunity...", icon: <Lightbulb className="h-5 w-5 text-red-600" /> },
    { message: "Building strategic framework...", icon: <Target className="h-5 w-5 text-red-600" /> },
    { message: "Evaluating market potential...", icon: <TrendingUp className="h-5 w-5 text-red-600" /> },
    { message: "Creating innovation canvas...", icon: <Rocket className="h-5 w-5 text-red-600" /> }
  ];
  
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sm:p-8 max-w-md mx-auto text-center">
      <div className="mb-8">
        <div className="relative mx-auto w-20 h-20 sm:w-28 sm:h-28">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              className="text-gray-200"
              strokeWidth="6"
              stroke="currentColor"
              fill="transparent"
              r="42"
              cx="50"
              cy="50"
            />
            <circle
              className="text-red-600 transition-all duration-500 ease-out"
              strokeWidth="6"
              strokeDasharray={264}
              strokeDashoffset={264 - (progress / 100) * 264}
              strokeLinecap="round"
              stroke="currentColor"
              fill="transparent"
              r="42"
              cx="50"
              cy="50"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">{Math.round(progress)}%</div>
              <div className="text-xs text-gray-500">Complete</div>
            </div>
          </div>
        </div>
      </div>
      
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
        Creating Strategy Canvas
      </h3>
      
      <div className="inline-flex items-center gap-3 bg-red-50 text-red-700 px-4 py-2 sm:px-5 sm:py-3 rounded-full mb-6 border border-red-200">
        {steps[step - 1].icon}
        <span className="font-medium text-sm sm:text-base">{steps[step - 1].message}</span>
      </div>
      
      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
        This takes about 30 seconds while we analyze your innovation concept and create a comprehensive strategic framework.
      </p>
    </div>
  );
};

// Email Capture with mobile improvements
const EmailCapture = ({ onSubmit, isSubmitting }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  
  const handleSubmit = () => {
    if (!email || !email.includes('@')) return;
    onSubmit({ email, name });
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <FileText className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Get Complete Strategy Report</h3>
        <p className="text-gray-600 text-sm">Download your innovation roadmap with detailed implementation timeline and success metrics</p>
      </div>
      
      <div className="mb-6 space-y-3">
        <h4 className="font-medium text-gray-900 text-sm">Your report includes:</h4>
        {[
          "Detailed implementation roadmap with timelines",
          "Risk mitigation strategies and contingency plans", 
          "Funding requirements and resource recommendations",
          "Market validation framework and customer research guide"
        ].map((item, idx) => (
          <div key={idx} className="flex items-start gap-3 text-sm text-gray-600">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="leading-relaxed">{item}</span>
          </div>
        ))}
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="name-input" className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <div>
          <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={!email || !email.includes('@')}
          isLoading={isSubmitting}
          icon={<Send className="h-4 w-4" />}
          fullWidth
        >
          Send Strategy Report
        </Button>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        We respect your privacy and never share your information
      </p>
    </div>
  );
};

// Success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">Strategy Report Sent Successfully!</h3>
      <p className="text-gray-600 mb-4">
        Check your email at <span className="font-semibold text-gray-900">{email}</span> for your complete innovation strategy report with implementation guidelines.
      </p>
      <p className="text-gray-600 text-sm">
        Our innovation experts will follow up to discuss implementation strategy and help you bring your concept to market.
      </p>
    </div>
  );
};

// Example innovation card with better mobile design
const ExampleCard = ({ example, onClick }) => {
  return (
    <button
      onClick={() => onClick(example.description)}
      className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-red-300 hover:shadow-sm transition-all w-full group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg border ${example.color} flex items-center justify-center`}>
          {example.icon}
        </div>
        <div className="text-xs text-gray-500 group-hover:text-red-600 transition-colors font-medium">
          Try this example →
        </div>
      </div>
      <div className="mb-2">
        <p className="text-sm font-medium text-gray-900 mb-1">{example.title}</p>
        <span className="text-xs text-gray-500 font-medium">{example.category}</span>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed">
        {example.description}
      </p>
    </button>
  );
};

// Main component
const InnovationCanvasTool = () => {
  const [innovationIdea, setInnovationIdea] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canvas, setCanvas] = useState(null);
  const [toast, setToast] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [analysisTitle, setAnalysisTitle] = useState('');
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const generateInnovationCanvas = async (ideaDescription) => {
    setIsLoading(true);
    setAnalysisTitle(ideaDescription);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 4000,
          temperature: 0.7,
          messages: [
            {
              role: 'user',
              content: `Create an Innovation Strategy Canvas for this concept: "${ideaDescription}"

Generate an 8-section strategic framework covering:

1. **Problem & Opportunity** - What problem does this solve and what's the market opportunity?
2. **Solution Concept** - How does this innovation work and what makes it unique?
3. **Value Proposition** - What specific value does it deliver to customers?
4. **Target Market** - Who are the ideal customers and what's the market size?
5. **Key Resources** - What resources, capabilities, and partnerships are needed?
6. **Revenue Model** - How will this generate revenue and what's the business model?
7. **Success Metrics** - How will success be measured and what are the key indicators?
8. **Key Risks** - What are the main risks and how can they be addressed?

For each section, provide:
- Brief summary (1 sentence)
- Description (2-3 sentences explaining the key concept)
- 3 key points
- 3 specific action items

Make this realistic and actionable. Keep language simple and business-focused.

Respond in this exact JSON format:
{
  "canvas": {
    "problem": {
      "summary": "Brief one-sentence summary",
      "description": "2-3 sentence description explaining the problem and opportunity",
      "keyPoints": [
        "Key point 1",
        "Key point 2", 
        "Key point 3"
      ],
      "actions": [
        "Specific action item 1",
        "Specific action item 2",
        "Specific action item 3"
      ]
    },
    "solution": {
      "summary": "Brief solution summary",
      "description": "2-3 sentence description of the solution concept",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "actions": ["Action 1", "Action 2", "Action 3"]
    },
    "value": {
      "summary": "Brief value summary",
      "description": "2-3 sentence description of value proposition",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "actions": ["Action 1", "Action 2", "Action 3"]
    },
    "market": {
      "summary": "Brief market summary",
      "description": "2-3 sentence description of target market",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "actions": ["Action 1", "Action 2", "Action 3"]
    },
    "resources": {
      "summary": "Brief resources summary",
      "description": "2-3 sentence description of key resources",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "actions": ["Action 1", "Action 2", "Action 3"]
    },
    "revenue": {
      "summary": "Brief revenue summary",
      "description": "2-3 sentence description of revenue model",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "actions": ["Action 1", "Action 2", "Action 3"]
    },
    "metrics": {
      "summary": "Brief metrics summary",
      "description": "2-3 sentence description of success metrics",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "actions": ["Action 1", "Action 2", "Action 3"]
    },
    "risks": {
      "summary": "Brief risks summary",
      "description": "2-3 sentence description of key risks",
      "keyPoints": ["Point 1", "Point 2", "Point 3"],
      "actions": ["Action 1", "Action 2", "Action 3"]
    }
  }
}`
            }
          ]
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate innovation canvas');
      }

      const data = await response.json();
      const resultContent = data.content[0].text;
      
      const jsonMatch = resultContent.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const canvasData = JSON.parse(jsonMatch[0]);
        
        setTimeout(() => {
          setCanvas(canvasData.canvas || {});
          setIsLoading(false);
        }, 2500);
      } else {
        throw new Error('Failed to parse canvas data');
      }
    } catch (error) {
      console.error('Error generating innovation canvas:', error);
      setIsLoading(false);
      setToast({
        message: 'Unable to generate innovation canvas. Please try again.',
        type: 'error'
      });
    }
  };

  const handleAnalysis = () => {
    if (!innovationIdea.trim()) {
      setToast({
        message: 'Please describe your innovation idea first.',
        type: 'error'
      });
      return;
    }
    generateInnovationCanvas(innovationIdea);
  };

  const handleEmailSubmit = (data) => {
    setIsEmailSubmitting(true);
    setTimeout(() => {
      setIsEmailSubmitting(false);
      setEmailSubmitted(true);
      setSubmittedEmail(data.email);
      setToast({
        message: 'Strategy report sent to your email successfully!',
        type: 'success'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };

  const handleReset = () => {
    setInnovationIdea('');
    setCanvas(null);
    setEmailSubmitted(false);
    setSubmittedEmail('');
    setAnalysisTitle('');
    setExpandedSection(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectExample = (description) => {
    setInnovationIdea(description);
    const textarea = document.getElementById('innovation-input');
    if (textarea) {
      setTimeout(() => {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }, 100);
    }
  };

  // SEO metadata (would be handled by helmet in real implementation)
  useEffect(() => {
    document.title = "Innovation Canvas Tool - Strategic Planning & Validation Framework | Lusidea";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Transform innovation ideas into structured strategies. Create comprehensive innovation canvas covering market opportunity, implementation roadmap, and success metrics for strategic planning.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header Section */}
      <header className="bg-white pt-24 sm:pt-28 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <Rocket className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Innovation Canvas Tool
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Transform your innovation ideas into structured strategies with comprehensive analysis covering market opportunity, implementation roadmap, and success metrics
          </p>
          
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 mt-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-red-600" />
              <span>Strategic Framework</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <span>Market Analysis</span>
            </div>
            <div className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-purple-600" />
              <span>Implementation Plan</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Input Section */}
        {!isLoading && !canvas && !emailSubmitted && (
          <div className="space-y-8">
            {/* Primary Input */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Describe Your Innovation Idea
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Tell us about your innovation concept and we'll create a comprehensive strategic canvas to help you develop and validate it.
              </p>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="innovation-input" className="block text-sm font-medium text-gray-700 mb-3">
                    Innovation concept or business idea
                  </label>
                  <textarea
                    id="innovation-input"
                    value={innovationIdea}
                    onChange={(e) => setInnovationIdea(e.target.value)}
                    placeholder="Describe your innovation idea, new product concept, or strategic initiative. For example: 'An intelligent system that handles customer service inquiries automatically using natural language processing' or 'A sustainable packaging solution made from seaweed that replaces plastic for e-commerce' or 'A virtual reality platform for remote team collaboration'..."
                    className="w-full p-4 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none min-h-32 text-gray-800 resize-y"
                    rows={6}
                  />
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-3 gap-2">
                    <span className="text-sm text-gray-500">Be specific about your innovation concept for better strategic analysis</span>
                    <span className="text-sm text-gray-400">{innovationIdea.length} characters</span>
                  </div>
                </div>
                
                <Button
                  onClick={handleAnalysis}
                  disabled={!innovationIdea.trim()}
                  icon={<Rocket className="h-5 w-5" />}
                  fullWidth
                  size="lg"
                >
                  Create Innovation Canvas
                </Button>
              </div>
            </section>

            {/* Example Suggestions */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                Need inspiration? Try these innovation examples:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {INNOVATION_EXAMPLES.map((example, idx) => (
                  <ExampleCard
                    key={idx}
                    example={example}
                    onClick={handleSelectExample}
                  />
                ))}
              </div>
            </section>
            
            {/* How it works */}
            <section className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-3">How this tool works</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Our analysis system examines your innovation concept and creates a comprehensive strategic canvas covering 8 key areas: 
                    problem definition, solution concept, value proposition, target market, resources needed, revenue model, success metrics, and risk assessment. 
                    Each section includes actionable insights and next steps for development.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Loading State */}
        {isLoading && <LoadingAnimation />}

        {/* Canvas Results */}
        {!isLoading && canvas && !emailSubmitted && (
          <div className="space-y-8">
            {/* Results Header */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Innovation Canvas Complete</h2>
                  <p className="text-gray-600 text-sm">
                    Strategic framework for: <span className="font-medium">"{analysisTitle.slice(0, 100)}..."</span>
                  </p>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-4 border border-red-200 mt-4">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Strategic Overview:</span> Your innovation canvas covers all critical aspects from problem definition to implementation roadmap. Use these insights to validate your concept, secure resources, and develop a launch strategy.
                </p>
              </div>
            </section>

            {/* Canvas Sections Overview */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-center">Innovation Canvas Sections</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(CANVAS_SECTIONS).map(([key, section]) => {
                  const SectionIcon = section.icon;
                  return (
                    <div key={key} className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50">
                      <div className={`w-10 h-10 rounded-lg ${section.iconBg} flex items-center justify-center mb-2`}>
                        <SectionIcon className={`h-5 w-5 ${section.textClass}`} />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{section.title}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Innovation Canvas Grid */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(canvas).map(([sectionKey, section]) => (
                <CanvasSection
                  key={sectionKey}
                  sectionKey={sectionKey}
                  section={section}
                  isExpanded={expandedSection === sectionKey}
                  onToggle={(key) => setExpandedSection(expandedSection === key ? null : key)}
                />
              ))}
            </section>

            {/* Email Capture */}
            <section className="max-w-2xl mx-auto">
              <EmailCapture 
                onSubmit={handleEmailSubmit}
                isSubmitting={isEmailSubmitting}
              />
            </section>

            {/* Strategy Session CTA */}
            <section className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl border border-red-200 p-6 sm:p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Need Strategy Consultation?</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Discuss implementation strategy and get expert guidance on bringing your innovation to market. 
                  Our innovation experts will help you refine your approach and create a detailed action plan.
                </p>
                <a
                  href="https://calendly.com/lusidea/innovation-strategy-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-sm"
                >
                  <Calendar className="h-5 w-5" />
                  Schedule Strategy Session
                </a>
              </div>
            </section>

            {/* Reset Action */}
            <div className="text-center pt-4">
              <Button
                onClick={handleReset}
                variant="outline"
                icon={<RefreshCw className="h-4 w-4" />}
              >
                Analyze New Innovation
              </Button>
            </div>
          </div>
        )}

        {/* Success State */}
        {emailSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
      
      <style jsx global>{`
        /* Enhanced focus states for accessibility */
        input:focus, textarea:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        /* Smooth transitions */
        * {
          transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
      `}</style>
    </div>
  );
};

export default InnovationCanvasTool;